/**
 * Patient Context Bar Component
 *
 * Displays patient information with a collapsing bar and hosts the shared
 * `PatientContextIndicatorsGroup` (indicators + popovers + edit modals).
 *
 * Indicator row logic lives in PatientContextIndicatorsGroup — this file
 * only owns the bar chrome (layout, dismiss button, actions slot).
 */

import React from 'react';
import { EligibilityBadge } from '@features/eligibility-checks';
import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Collapse, IconButton, Tooltip } from '@mui/material';
import type { Patient } from '@ts/types';

import { PatientContextIndicatorsGroup } from './PatientContextIndicatorsGroup';
import { PatientDisplay } from './PatientDisplay';

export interface PatientContextBarProps {
  /** Patient to display context for */
  patient: Patient | null;
  /** Callback when patient is changed (unselected) */
  onPatientChange?: (patient: Patient | undefined) => void;
  /** Callback when viewing last encounter note */
  onViewLastEncounterNote?: (noteId: string) => void;
  /** Callback when patient name is clicked (e.g., to navigate to patient profile) */
  onPatientClick?: (patientId: string) => void;
  /** Whether patient selection can be edited */
  editable?: boolean;
  /** Optional content rendered right-aligned in the bar (e.g. JotRx CTA) */
  actions?: React.ReactNode;
  /** Source identifier for analytics (where the bar is rendered) */
  source: string;
  /** Whether the bar has been dismissed (controlled externally) */
  dismissed?: boolean;
  /** Callback when dismiss button is clicked (only shown when no patient) */
  onDismiss?: () => void;
  /** Tooltip text for the dismiss button */
  dismissTooltip?: string;
}

/**
 * Patient Context Bar
 *
 * Displays patient information with context indicators that show
 * detailed information on hover (medications, diagnoses, allergies,
 * last encounter, treatment plan).
 */
export const PatientContextBar: React.FC<PatientContextBarProps> = ({
  patient,
  onPatientChange,
  onViewLastEncounterNote,
  onPatientClick,
  editable = true,
  actions,
  source,
  dismissed = false,
  onDismiss,
  dismissTooltip,
}) => {
  const handlePatientClose = () => {
    if (onPatientChange) {
      onPatientChange(undefined);
    }
  };

  const showBar = !dismissed && (!!patient || !!actions);

  return (
    <Collapse in={showBar}>
      <Box
        data-testid="patient-context-bar"
        sx={{
          backgroundColor: '#FCFCFC',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            maxWidth: 1400,
            mx: 'auto',
          }}
        >
          {patient && (
            <>
              <PatientDisplay
                patient={patient}
                onClose={editable ? handlePatientClose : undefined}
                onPatientClick={onPatientClick}
                source={source}
              />
              <EligibilityBadge
                patientId={patient.patient_id}
                source="patient_context_bar"
                showActionLabel={false}
              />
              <PatientContextIndicatorsGroup
                patientId={patient.patient_id}
                source={source}
                onViewLastEncounterNote={onViewLastEncounterNote}
              />
            </>
          )}
          {actions && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
              {actions}
            </Box>
          )}
          {!patient && onDismiss && (
            <Tooltip title={dismissTooltip ?? ''}>
              <IconButton
                aria-label="Dismiss patient bar"
                onClick={onDismiss}
                size="small"
                sx={{
                  ml: 'auto',
                  flexShrink: 0,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Collapse>
  );
};
